// A readable text colour for an arbitrary background, shared by the admin
// branding derivation and the client portal's content editor (button text,
// section-styled editing surfaces). Kept out of color.ts so that module stays
// pure parsing/contrast math.

import { contrastRatio } from "./color";
import { formatOklch, type OklchColor } from "./oklch";

const BLACK: OklchColor = { l: 0, c: 0, h: 0, a: 1 };
const WHITE: OklchColor = { l: 1, c: 0, h: 0, a: 1 };

/** WCAG AA minimum contrast for normal text — the default derivation target. */
export const READABLE_TEXT_MIN_RATIO = 4.5;

/**
 * A readable text colour for the given background: near-black or near-white,
 * escalating to pure black/white when the softer shade misses the target.
 * Falls back to whichever extreme contrasts more (a mid-tone background can
 * make AAA unreachable — contrast checkers will still surface that).
 */
export function readableTextOn(background: string, minRatio: number): string {
  const candidates: OklchColor[] = [
    { l: 0.24, c: 0, h: 0, a: 1 }, // comfortable near-black
    { l: 0.985, c: 0, h: 0, a: 1 }, // comfortable near-white
    BLACK,
    WHITE,
  ];
  let best = BLACK;
  let bestRatio = 0;
  for (const candidate of candidates) {
    const ratio = contrastRatio(formatOklch(candidate), background) ?? 0;
    if (ratio >= minRatio) return formatOklch(candidate);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }
  return formatOklch(best);
}
